import React, { Suspense } from 'react'
import { Global } from '@emotion/core'
import { Switch, Route, Router, NotFound, Redirect } from '@fs/zion-router'
import ErrorBoundary from '@fs/zion-error-boundary'
import { useUser } from '@fs/zion-user'
import { useExperiment } from '@fs/zion-experiments'
import { getSessionId } from '@fs/zion-session'
import { DirectedMessageProvider } from './components/wss/DirectedMessageProvider'
import PageLoadingIndicator from './components/shared/components/PageLoadingIndicator'
import { useDSCState } from './components/state/DSCStore'
import AamLandingpage from './components/aam/AppAam/components/AamLandingPage'
import AAM from './components/aam/AppAam/AppAam'
import { getSessionStorageItem } from './components/shared/helpers/sessionStorage'
import appModeNoSelectCss from './components/shared/helpers/appModeNoSelectGlobalCss'
import useDSApplicationMode from './components/shared/hooks/useDSApplicationMode'

const DiscoveryLandingPage = React.lazy(() =>
  import('./components/dlp/DiscoveryLandingPage/DiscoveryLandingPage/DiscoveryLandingPage')
)
const CAF = React.lazy(() => import('./components/caf/AppCaf/AppCaf'))
const Combo = React.lazy(() => import('./components/combo/AppCombo'))
const MCS = React.lazy(() => import('./components/mcs/controllers/McsMain'))
const WCF = React.lazy(() => import('./components/wcf/AppWcf/AppWcf'))
const PMH = React.lazy(() => import('./components/pmh/AppPmh/AppPmh'))
const POH = React.lazy(() => import('./components/poh/AppPoh/AppPoh'))
const WSS = React.lazy(() => import('./components/wss/AppWss/AppWss'))
const MFC = React.lazy(() => import('./components/mfc/AppMfc'))
const MfcLandingpage = React.lazy(() => import('./components/mfc/components/MfcLandingPage/MfcLandingPage'))
const RMS = React.lazy(() => import('./components/rms/AppRms/AppRms'))
const CAR = React.lazy(() => import('./components/car/AppCar/AppCar'))
const RmsLandingPage = React.lazy(() => import('./components/rms/AppRms/components/RmsLandingPage/RmsLandingPage'))
const RmsStandAloneLogin = React.lazy(() => import('./components/rms/AppRms/components/LoginPage/LoginPage'))
const WcfLandingpage = React.lazy(() => import('./components/wcf/AppWcf/components/WCFLandingPage/WCFLandingPage'))
const CafLandingPage = React.lazy(() => import('./components/caf/AppCaf/components/CAFLandingPage'))
const PmhLandingpage = React.lazy(() => import('./components/pmh/AppPmh/components/PMHLandingPage'))
const PohLandingPage = React.lazy(() => import('./components/poh/AppPoh/components/routes/PohLandingPage'))

const KioskActivationPage = React.lazy(() => import('./components/shared/components/KioskActivationPage'))

const KioskAttract = React.lazy(() => import('./components/shared/components/KioskAttract'))

const componentWithProps = (Component, props = {}) => () => <Component {...props} />

const ConditionalComponent = (Landingpage, LoggedInPage, landingPagePath) => {
  // Allows for using the same route but use of different components depending on logged in state
  let DisplayPage = NotFound

  const sessionId = getSessionId()
  const user = useUser()
  const dscState = useDSCState()
  const redirectToGuestPage = getSessionStorageItem('redirectToGuestPage')
  const displayAuthenticatedContent = user?.signedIn && !user.userLoading
  const appMode = useDSApplicationMode()
  const dislayNonStandardWebUserContent =
    sessionId &&
    !user?.userLoading &&
    !user.signedIn &&
    (appMode !== 'WEB' ||
      (dscState?.nonstandardUser?.initialized && redirectToGuestPage) ||
      (dscState?.nonstandardUser?.initialized && window.location.pathname !== landingPagePath))

  const displaySignIn = !user?.signedIn && !user?.userLoading
  const redirectToSignIn = !sessionId && window.location.pathname !== landingPagePath

  if (displayAuthenticatedContent) {
    DisplayPage = LoggedInPage
  }

  if (displaySignIn) {
    DisplayPage = Landingpage
  }

  if (dislayNonStandardWebUserContent) {
    DisplayPage = componentWithProps(LoggedInPage, { nonstandardUser: dscState.nonstandardUser })
  }

  if (user.userLoading) {
    DisplayPage = PageLoadingIndicator
  }

  if (redirectToSignIn) {
    // If we don't have an auth or unauth session id, redirect to the landing page
    DisplayPage = componentWithProps(Redirect, { to: landingPagePath })
  }

  return componentWithProps(DisplayPage)
}

// Can be reused by passing a component param in once other landing pages are built
const ConditionalBaseRoute = (Landingpage, toURL) => {
  const user = useUser()
  if (user?.signedIn) {
    return componentWithProps(Redirect, { to: toURL })
  }
  return componentWithProps(Landingpage)
}

function ExternalRoute(link) {
  window.location = link
  return <>/</>
}

function DsApp() {
  const [wipExperiment] = useExperiment('DiscoveryWIPApps')

  return (
    <Router>
      <ErrorBoundary>
        <DirectedMessageProvider>
          <Global styles={appModeNoSelectCss} />
          <Suspense fallback={<PageLoadingIndicator />}>
            <Switch>
              {wipExperiment && <Route exact path="(/|/discovery/landing)" component={DiscoveryLandingPage} />}
              <Route exact path="/discovery/mcs" render={() => <MCS />} />
              <Route
                exact
                path="/discovery/(explore|where-am-i-from)"
                render={ConditionalBaseRoute(WcfLandingpage, '/discovery/explore/generations')}
              />
              {wipExperiment && (
                <Route
                  compare
                  path="/discovery/compareme"
                  render={ConditionalComponent(CafLandingPage, CAF, '/discovery/compareme')}
                />
              )}
              <Route compare path="/discovery/combo" render={() => <Combo />} />
              <Route
                path="/discovery/famousrelatives"
                render={ConditionalComponent(MfcLandingpage, MFC, '/discovery/famousrelatives')}
              />
              <Route exact path="/discovery/recordme/standalone" component={RmsStandAloneLogin} />
              <Route
                path="/discovery/recordme"
                render={ConditionalComponent(RmsLandingPage, RMS, '/discovery/recordme')}
              />
              <Redirect from="/discovery/record" to="/discovery/recordme" />
              <Route path="/discovery/explore" render={() => <WCF />} />
              <Route
                path="/discovery/about/correctionslogin"
                render={ConditionalComponent(AamLandingpage, AAM, '/discovery/about/correctionslogin')}
              />
              <Route path="/discovery/about" render={ConditionalComponent(AamLandingpage, AAM, '/discovery/about')} />

              <Route path="/discovery/activatekiosk" render={() => <KioskActivationPage />} />
              <Route path="/discovery/kioskattract" render={() => <KioskAttract />} />
              <Route
                path="/discovery/picture"
                render={ConditionalComponent(PmhLandingpage, PMH, '/discovery/picture')}
              />
              <Route
                path="/discovery/pictureour"
                render={ConditionalComponent(PohLandingPage, POH, '/discovery/pictureour')}
              />
              {wipExperiment && <Route path="/discovery/call" render={() => <CAR />} />}
              <Route path="/discovery/websockets" render={() => <WSS />} />
              <Route exact path="/discoverycenter" render={() => ExternalRoute('/discovery/schedule')} />
              <Route
                path="/discoverycenter/seattle"
                render={() => ExternalRoute('/discovery/schedule/?center=SEATTLE')}
              />
              <Route path="/discovery/seattle" render={() => ExternalRoute('/discovery/schedule/?center=SEATTLE')} />
              <Route path="/discoverycenter/fhl" render={() => ExternalRoute('/discovery/schedule/?center=FHL')} />
              <Route path="/discovery/fhl" render={() => ExternalRoute('/discovery/schedule/?center=FHL')} />
              <Route render={() => <NotFound />} />
            </Switch>
          </Suspense>
        </DirectedMessageProvider>
      </ErrorBoundary>
    </Router>
  )
}
export default DsApp

DsApp.propTypes = {}
